import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKBDeflection } from '../../../hooks/useKBDeflection';
import { classifyTicket, getTimeContext } from '../../../utils/ticketIntelligence';
import { ArticlePreviewDrawer } from './ArticlePreviewDrawer';
import type { Article } from '../../../types/knowledgeBase';
import './HelpRequestForm.css';

interface HelpRequestFormProps {
  onSubmit: (description: string) => Promise<void>;
  isSubmitting: boolean;
}

const MAX_DESCRIPTION_LENGTH = 500;

export function HelpRequestForm({ onSubmit, isSubmitting }: HelpRequestFormProps) {
  const navigate = useNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [description, setDescription] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [showDeflection, setShowDeflection] = useState(false);
  const [previewArticle, setPreviewArticle] = useState<Article | null>(null);

  const { result: kbResult, search: searchKB, clearSearch } = useKBDeflection({
    debounceMs: 400,
    maxResults: 4,
    minQueryLength: 10,
  });

  // Get classification preview for visual feedback
  const classification = description.length >= 10
    ? classifyTicket({ description, timeOfDay: getTimeContext() })
    : null;

  // Search KB as user types
  useEffect(() => {
    if (description.length >= 10) {
      searchKB(
        description,
        classification?.category ?? null,
        classification?.ticketType === 'Incident'
      );
    } else {
      clearSearch();
      setShowDeflection(false);
    }
  }, [description, classification?.category, classification?.ticketType, searchKB, clearSearch]);

  // Show deflection panel when we have results
  useEffect(() => {
    if (kbResult.hasSearched && kbResult.articles.length > 0) {
      setShowDeflection(true);
    }
  }, [kbResult.hasSearched, kbResult.articles.length]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(120, Math.min(textarea.scrollHeight, 300))}px`;
    }
  }, [description]);

  const handleSubmit = async () => {
    if (description.trim().length < 10 || isSubmitting) return;
    await onSubmit(description.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Ctrl+Enter or Cmd+Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Open article preview drawer instead of navigating
  const handleArticleClick = useCallback((article: Article) => {
    setPreviewArticle(article);
  }, []);

  // Close the preview drawer
  const handleClosePreview = useCallback(() => {
    setPreviewArticle(null);
  }, []);

  // User confirmed article resolved their issue
  const handleArticleResolved = useCallback((article: Article) => {
    setPreviewArticle(null);
    // Navigate to success state - article resolved the issue, no ticket needed
    navigate('/help', {
      state: {
        deflected: true,
        resolvedByArticle: article.title
      }
    });
  }, [navigate]);

  // User indicated article did not resolve their issue
  const handleArticleNotResolved = useCallback(() => {
    setPreviewArticle(null);
    // Stay on form - user can continue with their ticket submission
    // Their description is preserved
  }, []);

  const handleDeflectionSuccess = () => {
    navigate('/help', { state: { deflected: true } });
  };

  const canSubmit = description.trim().length >= 10 && !isSubmitting;

  return (
    <div className="help-request-form">
      {/* Main heading */}
      <div className="help-request-form__header">
        <h1 className="help-request-form__title">How can we help?</h1>
        <p className="help-request-form__subtitle">
          Describe what you need in your own words. We'll take it from there.
        </p>
      </div>

      {/* Main input area */}
      <div className={`help-request-form__input-container ${isFocused ? 'help-request-form__input-container--focused' : ''}`}>
        <textarea
          ref={textareaRef}
          className="help-request-form__textarea"
          placeholder="Examples:&#10;• My laptop won't connect to VPN&#10;• I need access to the Marketing shared drive&#10;• Teams keeps crashing when I join meetings"
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
          maxLength={MAX_DESCRIPTION_LENGTH}
          aria-label="Describe what you need help with"
        />

        {/* Character counter */}
        <div className={`help-request-form__char-count ${description.length > MAX_DESCRIPTION_LENGTH * 0.9 ? 'help-request-form__char-count--warning' : ''}`}>
          {description.length}/{MAX_DESCRIPTION_LENGTH}
        </div>

        {/* Smart detection indicator */}
        {classification && description.length >= 15 && (
          <div className="help-request-form__detection">
            <span className="help-request-form__detection-label">
              We'll route this to the right team
            </span>
            {classification.suggestedTags.length > 0 && (
              <div className="help-request-form__tags">
                {classification.suggestedTags.map((tag) => (
                  <span key={tag} className="help-request-form__tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* KB Deflection - Helpful articles */}
      {showDeflection && kbResult.articles.length > 0 && (
        <div className="help-request-form__deflection">
          <div className="help-request-form__deflection-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="10" cy="10" r="8" />
              <path d="M10 6v1M10 10v4" strokeLinecap="round" />
            </svg>
            <span>These articles might help</span>
          </div>
          <div className="help-request-form__deflection-articles">
            {kbResult.articles.map((article) => (
              <button
                key={article.id}
                type="button"
                className="help-request-form__deflection-article"
                onClick={() => handleArticleClick(article)}
              >
                <span className="help-request-form__deflection-article-title">
                  {article.title}
                </span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ))}
          </div>
          <div className="help-request-form__deflection-actions">
            <button
              type="button"
              className="help-request-form__deflection-solved"
              onClick={handleDeflectionSuccess}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 8l4 4 6-8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Found my answer
            </button>
            <button
              type="button"
              className="help-request-form__deflection-dismiss"
              onClick={() => setShowDeflection(false)}
            >
              Continue with request
            </button>
          </div>
        </div>
      )}

      {/* Submit section */}
      <div className="help-request-form__submit-section">
        <button
          type="button"
          className="help-request-form__submit-btn"
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-label="Submit help request"
        >
          {isSubmitting ? (
            <>
              <div className="help-request-form__spinner" />
              Submitting...
            </>
          ) : (
            <>
              Submit Request
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9h12M11 5l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </>
          )}
        </button>
        <p className="help-request-form__submit-hint">
          {description.length < 10
            ? `${10 - description.length} more characters needed`
            : 'Press Ctrl+Enter to submit'}
        </p>
      </div>

      {/* Trust indicators */}
      <div className="help-request-form__trust">
        <div className="help-request-form__trust-item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 4a.75.75 0 011.5 0v3.25l2.22 1.28a.75.75 0 01-.75 1.3L7.7 9.55A.75.75 0 017.25 9V5z" />
          </svg>
          <span>Average response: 2 hours</span>
        </div>
        <div className="help-request-form__trust-item">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm3.22 5.47l-4 4a.75.75 0 01-1.06 0l-2-2a.75.75 0 111.06-1.06l1.47 1.47 3.47-3.47a.75.75 0 111.06 1.06z" />
          </svg>
          <span>98% resolved same day</span>
        </div>
      </div>

      {/* Article Preview Drawer */}
      <ArticlePreviewDrawer
        article={previewArticle}
        isOpen={previewArticle !== null}
        onClose={handleClosePreview}
        onResolved={handleArticleResolved}
        onNotResolved={handleArticleNotResolved}
      />
    </div>
  );
}
